import { TIERS } from '@/features/hierarchy/api'
import { tierForLevel } from './api'

/**
 * Turning "this role, at this place" into the per-permission scopes the API wants.
 *
 * ── The problem this exists to solve ─────────────────────────────────────
 * The API scopes a grant per PERMISSION, and every permission declares the
 * hierarchy levels it may attach at. Those declarations disagree with each
 * other, which is what made the first version of the invite form a wall of
 * pickers. From the seeded catalogue:
 *
 *   `assets.manage_assets`      all four levels
 *   `users.manage`              account or plant
 *   `sensors.manage_sensors`    plant, line or machine — NOT account
 *   `sensors.manage_connections` plant only
 *   `account.manage`            account only
 *
 * So the two answers a person actually wants to give are both invalid for most
 * of a role's permissions:
 *
 *   "the whole account" — refused by every plant-or-below permission, which is
 *   the majority of the catalogue.
 *   "Findlay" — refused by `account.manage` and `copilot.use`.
 *
 * Asking somebody to resolve that themselves, seventeen times, is asking them to
 * hold the catalogue in their head. This module resolves it instead, by one
 * rule, and the screen states what the rule did.
 *
 * ── The rule ─────────────────────────────────────────────────────────────
 * For each permission, a chosen place becomes:
 *
 *   1. itself, if the permission may attach at that level. The common case, and
 *      nothing is said about it.
 *   2. otherwise its nearest ANCESTOR at a level the permission allows. Picking
 *      Findlay for an account-only permission grants it account-wide. This
 *      WIDENS the grant, so it is never silent — `broadened` carries it and the
 *      card says so in words.
 *   3. otherwise every DESCENDANT at the shallowest level the permission
 *      allows. "The whole account" for a plant-level permission means every
 *      plant, which is what the person meant and what the API needs spelling
 *      out. It grants no more than was asked for, so it is reported as a count
 *      rather than as a warning.
 *   4. otherwise nothing — the permission cannot be granted there at all, and
 *      `impossible` carries it so the card can say which and why.
 *
 * ── Why widening is declared and narrowing is merely counted ────────────
 * They are not symmetrical. (2) hands somebody MORE access than the person
 * granting it asked for, which is the mistake nobody notices until an audit.
 * (3) hands them exactly what was asked for, spelled out differently. The first
 * needs a sentence; the second needs a number.
 *
 * ── Why every function here is async ────────────────────────────────────
 * It used to take an index built from `GET /assets/hierarchy`, which handed over
 * the whole estate at once. That endpoint is being withdrawn, so the ancestors
 * and descendants these rules need are now fetched a generation at a time
 * through `features/hierarchy/estate.js`. The rules themselves are unchanged —
 * what changed is that reading the tree is a request rather than a lookup, and
 * every caller therefore has a pending state to render.
 *
 * ── Why this is not on the server ───────────────────────────────────────
 * It could be, and arguably should — a second client will need the same rule,
 * and the walk is far cheaper where the rows already are. Recorded in
 * BACKEND-GAPS.md. Until then it is here, in one place, rather than spread
 * across the form.
 */

/** Deepest-last. The hierarchy's own order, which is also the scope columns'. */
export const tierDepth = (tier) => {
  const at = TIERS.indexOf(tier)
  return at < 0 ? -1 : at
}

/** The tiers a permission may attach at, as the hierarchy names them. */
export const allowedTiers = (permission) =>
  new Set((permission?.targetLevels ?? []).map(tierForLevel).filter(Boolean))

/**
 * One chosen place → the scopes one permission should carry for it.
 *
 * @param {object} place        A hierarchy node.
 * @param {object} permission   From the catalogue.
 * @param {object} estate       From `createEstate`.
 * @returns {Promise<{scopes: Array, outcome: 'exact'|'broadened'|'expanded'|'impossible', at?: object}>}
 */
export async function resolvePlace(place, permission, estate) {
  const allowed = allowedTiers(permission)
  if (!allowed.size) return { scopes: [], outcome: 'impossible' }

  // 1. The place itself.
  if (allowed.has(place.tier)) {
    return { scopes: [{ tier: place.tier, id: place.id }], outcome: 'exact' }
  }

  /* 2. Nearest allowed ancestor — deepest first, so the grant widens as little
        as it can. `ancestors` is root-first and includes the place itself last,
        which is dropped: rule 1 already answered that case. */
  const above = (await estate.ancestors(place)).slice(0, -1).reverse()
  const ancestor = above.find((entry) => allowed.has(entry.tier))
  if (ancestor) {
    return {
      scopes: [{ tier: ancestor.tier, id: ancestor.id }],
      outcome: 'broadened',
      at: ancestor,
    }
  }

  // 3. Every descendant at the shallowest allowed level below.
  const below = [...allowed]
    .filter((tier) => tierDepth(tier) > tierDepth(place.tier))
    .sort((a, b) => tierDepth(a) - tierDepth(b))

  for (const tier of below) {
    const found = await estate.descendantsAt(place, tier)
    if (found.length) {
      return {
        scopes: found.map((entry) => ({ tier: entry.tier, id: entry.id })),
        outcome: 'expanded',
      }
    }
  }

  /* 4. Nowhere to put it. A plant-only permission chosen against a machine
        reaches here, and so does one chosen against an account holding no
        plants. */
  return { scopes: [], outcome: 'impossible' }
}

/**
 * The whole grant for one role: chosen places → every permission's scopes, plus
 * what the screen has to say about it.
 *
 * Permissions are resolved in parallel and places serially within one: the
 * walks they share land in the estate's cache on first use, so a role whose
 * seventeen permissions all widen to the account costs one ancestor chain
 * rather than seventeen.
 *
 * @param {Array} places       Hierarchy nodes the person picked.
 * @param {Array} permissions  The role's permissions, from the catalogue.
 * @param {object} estate      From `createEstate`.
 */
export async function resolveRole(places, permissions, estate) {
  const permissionScopes = new Map()
  /* Permissions whose grant had to widen, and where to. One entry per
     permission rather than per place: "Manage Account applies account-wide" is
     one sentence however many places were chosen. */
  const broadened = []
  const expanded = []
  const impossible = []

  const resolved = await Promise.all(
    permissions.map(async (permission) => {
      const results = []
      for (const place of places) results.push(await resolvePlace(place, permission, estate))
      return { permission, results }
    }),
  )

  for (const { permission, results } of resolved) {
    const scopes = new Map()
    let widenedTo = null
    let expandedCount = 0
    let everFailed = false
    let everWorked = false

    for (const result of results) {
      for (const scope of result.scopes) scopes.set(`${scope.tier}:${scope.id}`, scope)

      if (result.outcome === 'broadened') widenedTo = result.at
      if (result.outcome === 'expanded') expandedCount += result.scopes.length
      if (result.outcome === 'impossible') everFailed = true
      else everWorked = true
    }

    permissionScopes.set(permission.id, scopes)

    if (widenedTo) broadened.push({ permission, at: widenedTo })
    if (expandedCount) expanded.push({ permission, count: expandedCount })
    /* Only when NO chosen place worked. A permission granted at one of two
       places is granted, and saying it failed would be wrong. */
    if (everFailed && !everWorked) impossible.push({ permission })
  }

  return { permissionScopes, broadened, expanded, impossible }
}

/**
 * Can this stored grant be described as "the role, at these places"?
 *
 * Asked when the editor opens, because an existing grant may have been written
 * by a form that scoped each permission separately — or by another client, or by
 * this one before the places were resolvable. If re-deriving from the union of
 * its scopes reproduces it exactly, the simple control can represent it and the
 * role opens in the simple view. If not, it opens in the per-permission view
 * rather than silently rewriting somebody's carefully-built grant.
 *
 * ── What it costs now ───────────────────────────────────────────────────
 * The stored scopes name ids and tiers and nothing else, so each distinct node
 * is read once to recover its name — `estate.ancestors` does that and the chain
 * above it in the same walk, which is what the rules need next. Then each
 * candidate set is replayed through `resolveRole`. Every walk after the first
 * hits the cache, so the cost is roughly one ancestor chain per distinct scope
 * plus, for a grant that widened, nothing at all.
 *
 * @returns {Promise<{places: Array}|null>} `null` when the grant needs the detailed view.
 */
export async function inferPlaces(permissionScopes, permissions, estate) {
  const union = new Map()
  for (const scopes of permissionScopes.values()) {
    for (const scope of scopes.values()) union.set(`${scope.tier}:${scope.id}`, scope)
  }

  /* Stored scopes carry no name. Recovering the node is what makes a chip
     readable and what the rules walk from. A scope naming a node that no longer
     exists — a site deleted after the grant was written — drops out rather than
     failing the whole inference: the remaining places either still describe the
     grant or they do not, and that question is already being asked below. */
  const nodes = await Promise.all(
    [...union.values()].map(async (scope) => {
      try {
        return (await estate.ancestors({ tier: scope.tier, id: scope.id })).at(-1) ?? null
      } catch {
        return null
      }
    }),
  )

  const places = nodes
    .filter(Boolean)
    /* Shallowest first: a grant that came from "the whole account" carries the
       account plus, on other permissions, every plant. Resolving from the
       account alone reproduces both, so trying it first is what lets the common
       case collapse back to one chip instead of a dozen. */
    .sort((a, b) => tierDepth(a.tier) - tierDepth(b.tier))

  for (const candidate of candidateSets(places)) {
    const { permissionScopes: derived } = await resolveRole(candidate, permissions, estate)
    if (sameScopes(derived, permissionScopes)) return { places: candidate }
  }

  return null
}

/**
 * The place sets worth testing, simplest first.
 *
 * Each single place, then all of them together. NOT just the shallowest one and
 * the whole set, which was the first attempt and was wrong in the most ordinary
 * case there is: granting a role at Findlay stores `findlay` for most of its
 * permissions and `account` for the one that can only attach there, so the union
 * is `{account, findlay}` and neither the shallowest node alone nor both
 * together reproduces it. The answer is `findlay` alone — a single place that is
 * not the shallowest. A test caught it; the form would have reopened its own
 * grants in the detailed view.
 *
 * A match is by definition an equivalent description — the derived scopes have
 * to equal the stored ones exactly — so a candidate that happens to fit is a
 * correct way to say the same thing, not a guess.
 *
 * Still not a power set. Two shapes are worth recognising: one place, and
 * several. Anything else is genuinely per-permission and opens in the detailed
 * view, which is the right outcome rather than a failure.
 */
function candidateSets(places) {
  if (!places.length) return [[]]
  if (places.length === 1) return [places]
  return [...places.map((place) => [place]), places]
}

function sameScopes(a, b) {
  if (a.size !== b.size) return false
  for (const [permissionId, scopes] of a) {
    const other = b.get(permissionId)
    if (!other || other.size !== scopes.size) return false
    for (const key of scopes.keys()) if (!other.has(key)) return false
  }
  return true
}
